import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  useAllResellerOrders,
  useApproveResellerOrder,
  useActivateResellerOrder,
  type ResellerOrderStatus,
} from '@/hooks/useResellerOrders';
import { OrderStatusBadge, formatMoney } from '@/components/reseller/OrderStatusBadge';

export default function MasterResellerOrdersPage() {
  const { data: orders, isLoading } = useAllResellerOrders();
  const approve = useApproveResellerOrder();
  const activate = useActivateResellerOrder();
  const [status, setStatus] = useState<ResellerOrderStatus | 'all'>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return (orders ?? []).filter((o) => {
      if (status !== 'all' && o.status !== status) return false;
      if (search && !`${o.customer_email} ${o.customer_name ?? ''} ${o.order_number}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [orders, status, search]);

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Reseller Orders</h1>
        <p className="text-muted-foreground">Approve pending orders and activate to issue license keys.</p>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center gap-2 flex-wrap">
          <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
          <Select value={status} onValueChange={(v) => setStatus(v as ResellerOrderStatus | 'all')}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="activated">Activated</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="hold">On Hold</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground">No orders.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="font-mono text-xs">{o.order_number}</TableCell>
                    <TableCell>
                      <div className="font-medium">{o.customer_name ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">{o.customer_email}</div>
                    </TableCell>
                    <TableCell>{formatMoney(Number(o.amount), o.currency)}</TableCell>
                    <TableCell><OrderStatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {o.status === 'pending' && (
                          <Button size="sm" variant="outline" disabled={approve.isPending} onClick={() => approve.mutate(o.id)}>
                            Approve
                          </Button>
                        )}
                        {(o.status === 'approved' || o.status === 'pending') && (
                          <Button size="sm" disabled={activate.isPending} onClick={() => activate.mutate(o.id)}>
                            Activate
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
